import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { AppLayout } from '@/components/Layouts/AppLayout'
import { useUser } from '@/hooks/Contexts'
import { appUtils } from 'lib/AppUtils'
import { API_URL } from '@/utils/Consts'

const MAX_CONTENT = 2000
const MIN_CONTENT = 10

const ComplaintForm = () => {
  const user = useUser()

  const [email, setEmail] = useState('')
  const [content, setContent] = useState('')

  useEffect(() => {
    if (user?.email) setEmail(user.email)
  }, [user?.email])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const canSubmit = !isSubmitting && content.trim().length >= MIN_CONTENT && email.trim().length > 0

  const handleSubmit = async () => {
    setIsSubmitting(true)
    const res = await appUtils.postData(
      API_URL.complaint,
      {
        email: email.trim(),
        content: content.trim(),
        url: typeof window !== 'undefined' ? window.location.pathname : '/complaints',
      },
      {},
    )
    setIsSubmitting(false)
    if (res) {
      setSubmitted(true)
      toast.success('Your complaint has been submitted. We will respond within 5 working days.')
    } else {
      toast.error('Failed to submit. Please try again.')
    }
  }

  if (submitted) {
    return (
      <div className='rounded-lg border border-green-200 bg-green-50 p-6 text-center'>
        <svg
          className='mx-auto mb-3 h-12 w-12 text-green-500'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          viewBox='0 0 24 24'
        >
          <path strokeLinecap='round' strokeLinejoin='round' d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
        </svg>
        <p className='mb-1 font-semibold text-green-800'>Complaint submitted</p>
        <p className='text-sm text-green-700'>We will get back to you within 5 working days.</p>
      </div>
    )
  }

  return (
    <div>
      <h2 className='mb-4 text-base font-semibold text-gray-900'>Complaint Form</h2>

      <div className='mb-4'>
        <label htmlFor='complaint-email' className='mb-1 block text-sm font-medium text-gray-700'>
          Email address <span className='text-error'>*</span>
        </label>
        <input
          id='complaint-email'
          type='email'
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={isSubmitting}
          placeholder='your@email.com'
          className='w-full rounded-md border border-gray-300 p-3 text-sm focus:border-amber-v1 focus:outline-none focus:ring-1 focus:ring-amber-v1 disabled:bg-gray-50'
        />
      </div>

      <div className='mb-6'>
        <label htmlFor='complaint-content' className='mb-1 block text-sm font-medium text-gray-700'>
          Describe your complaint <span className='text-error'>*</span>
        </label>
        <textarea
          id='complaint-content'
          value={content}
          onChange={e => setContent(e.target.value.slice(0, MAX_CONTENT))}
          rows={6}
          placeholder='Please provide as much detail as possible, including order numbers or dates if relevant...'
          disabled={isSubmitting}
          className='w-full resize-none rounded-md border border-gray-300 p-3 text-sm focus:border-amber-v1 focus:outline-none focus:ring-1 focus:ring-amber-v1 disabled:bg-gray-50'
        />
        <div className='mt-1 flex justify-between text-xs text-gray-400'>
          <span>{content.trim().length < MIN_CONTENT ? `Minimum ${MIN_CONTENT} characters` : ''}</span>
          <span>
            {content.length}/{MAX_CONTENT}
          </span>
        </div>
      </div>

      <button
        type='button'
        onClick={handleSubmit}
        disabled={!canSubmit}
        className='btn-rd-amber btn-hover-amber h-11 px-8 text-base font-medium disabled:cursor-not-allowed disabled:opacity-50'
      >
        {isSubmitting ? 'Submitting...' : 'Submit Complaint'}
      </button>
    </div>
  )
}

const Complaints = () => (
  <AppLayout title='Complaints'>

    {/* Section 1 */}
    <div className='mb-8'>
      <h2 className='mb-2 text-base font-semibold text-gray-900'>Your feedback is important to us</h2>
      <p className='text-sm text-gray-700'>
        We are committed to resolving any issues quickly and fairly, and we will always treat your feedback with the
        care it deserves. Please use the form below or contact us directly — we aim to respond to all complaints within
        5 working days.
      </p>
    </div>

    {/* Section 2 */}
    <div className='mb-8'>
      <h2 className='mb-3 text-base font-semibold text-gray-900'>How to contact us</h2>
      <ul className='space-y-2 text-sm text-gray-700'>
        <li className='flex items-start gap-2'>
          <svg
            className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-v1'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
            />
          </svg>
          <span>
            <strong>Email:</strong>{' '}
            <a href='mailto:info@rkingscompetitions.com' className='text-amber-v1 hover:underline'>
              info@rkingscompetitions.com
            </a>
          </span>
        </li>
        <li className='flex items-start gap-2'>
          <svg
            className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-v1'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
            />
            <path strokeLinecap='round' strokeLinejoin='round' d='M15 11a3 3 0 11-6 0 3 3 0 016 0z' />
          </svg>
          <span>
            <strong>Post:</strong> R Kings Competitions, Unit B Mill Road, Newry, County Down, BT35 7DT, Northern
            Ireland
          </span>
        </li>
      </ul>
    </div>

    {/* Section 3 */}
    <div className='mb-8'>
      <h2 className='mb-2 text-base font-semibold text-gray-900'>If you need to escalate your complaint</h2>
      <p className='text-sm text-gray-700'>
        If you are not satisfied with our response, you may request that your complaint be reviewed by a senior
        manager, who will carry out a full and final review of your case.
      </p>
    </div>

    {/* Form — available to all users, guests enter email manually */}
    <div className='rounded-lg border border-gray-200 bg-white p-5 shadow-sm'>
      <ComplaintForm />
    </div>

  </AppLayout>
)

export default Complaints
